import React, { useState, useContext } from "react";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import ButtonUI from "../material-ui/ButtonUI";
import { TbCircleCheck } from "react-icons/tb";
import { AuthContext } from "../../context/Auth/AuthContext";
import { MdModeEdit } from "react-icons/md";
import InputUI from "../material-ui/InputUI";
import markClientUnknown from "../../api/markClientUnknown";
import { Grid } from "@mui/material";
import { RiCloseCircleFill } from "react-icons/ri";
import IconButton from "@mui/material/IconButton";

// Yup schema for validation with "either email or phone required"
const clientInfoSchema = yup.object().shape({
  email: yup.string().email("Email must be valid").notRequired(), // Email is optional, will be validated by the custom test
  phone: yup.string().notRequired(), // Phone is optional, will be validated by the custom test
});

const ConfirmToast = ({
  open,
  onClose,
  title,
  message,
  documentId,
  setDocData,
  setDocumentUpdatedToTrue,
  setIsClientUnknown,
}) => {
  const [showInputPopup, setShowInputPopup] = useState(false);
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [customError, setCustomError] = useState(""); // Custom error state for phone validation

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset, // Reset the form
    setError, // Manually set errors
  } = useForm({
    resolver: yupResolver(clientInfoSchema),
  });

  // Custom phone validation and handling
  const validateEitherEmailOrPhone = (data) => {
    setCustomError(""); // Reset the custom error before validation

    if (!data.email && !data.phone) {
      // If both email and phone are missing
      setCustomError("Either email or phone is required");
      return false;
    }

    // Custom validation for phone number (if provided)
    if (data.phone && data.phone.trim() !== "") {
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(data.phone)) {
        setCustomError("Phone number must be 10 digits.");
        return false; // Return false if the phone is invalid
      }
    }

    return true; // Return true if validation passes
  };

  const onEditClientDetailsFormSubmit = async (data) => {
    if (!validateEitherEmailOrPhone(data)) return;

    setLoading(true);

    try {
      const res = await markClientUnknown({
        documentID: documentId,
        token,
        email: data.email,
        phone: data.phone,
      });

      if (res.code === 200) {
        if (setDocumentUpdatedToTrue) {
          setDocumentUpdatedToTrue();
        }
        if (setDocData) {
          setDocData((data) => ({
            ...data,
            client_unknown: "1",
          }));
        }
        if (setIsClientUnknown) {
          setIsClientUnknown(true);
        }
        toast.success("Client Marked as Unknown", {
          position: "top-center",
          autoClose: 3000,
          theme: "dark",
        });
        setShowInputPopup(false); // Close second popup
        onClose(); // Close the first confirmation popup
        reset(); // Reset the form after successful submission
      } else {
        toast.error("Error while setting client as unknown", {
          position: "top-center",
          autoClose: 5000,
          theme: "dark",
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("An unexpected error occurred", {
        position: "top-center",
        autoClose: 5000,
        theme: "dark",
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmMarkClientUnknown = () => {
    reset(); // Reset form values before showing the second popup
    setShowInputPopup(true); // Show the second popup
    onClose(); // Close the first confirmation dialog
  };

  return (
    <>
      {/* Main Confirmation Dialog */}
      <Dialog open={open} onClose={onClose}>
        <DialogTitle id="alert-dialog-title" className="edit-doc-modal">
          <div className="popup-header-svg-icon">
            <TbCircleCheck />
          </div>
          <div className="popup-header-title">{title || "Confirmation"}</div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <ButtonUI
            formButton={false}
            variant="outlined"
            buttonText="No"
            onClickFunc={onClose}
            style={{
              backgroundColor: "#fff",
              color: "#1a6b7c",
              border: "1px solid #1a6b7c",
            }}
          />
          <ButtonUI
            formButton={false}
            variant="outlined"
            buttonText="Yes"
            onClickFunc={confirmMarkClientUnknown}
            autoFocus
            style={{
              backgroundColor: "#fff",
              color: "#1a6b7c",
              border: "1px solid #1a6b7c",
            }}
          />
        </DialogActions>
      </Dialog>

      {/* Second Popup for Email and Phone Number */}
      <Dialog open={showInputPopup} onClose={() => setShowInputPopup(false)}>
        <DialogTitle id="input-dialog-title" className="edit-doc-modal">
          <div className="popup-header-svg-icon">
            <MdModeEdit />
          </div>
          <div className="popup-header-title">
            Enter Document Source Information
          </div>
          <IconButton
            aria-label="close"
            onClick={() => setShowInputPopup(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#1a6b7c",
            }}
          >
            <RiCloseCircleFill />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Please add the document source Email or Phone Number
          </DialogContentText>

          {/* Form Fields */}
          <Grid className="edit-document">
            <div className="edit-document-form ">
              <div className="add-document-inputs">
                <div
                  className="add-document-inputs-with-error-message"
                  style={{ width: "100%" }}
                >
                  <InputUI
                    name="email"
                    label="Enter Email ID"
                    type="email"
                    variant="outlined"
                    cssStyles={{ margin: "10px 0px" }}
                    register={register}
                  />
                  <span className="error-message">
                    {errors["email"]?.message}
                  </span>
                </div>
              </div>
              <div className="add-document-inputs">
                <div
                  className="add-document-inputs-with-error-message"
                  style={{ width: "100%" }}
                >
                  <InputUI
                    name="phone"
                    label="Enter Phone Number"
                    type="text"
                    variant="outlined"
                    cssStyles={{ margin: "10px 0px" }}
                    register={register}
                    inputProps={{ maxLength: 10, inputMode: "numeric" }}
                    registerOptions={{
                      onChange: (e) => {
                        const digitsOnly = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);
                        if (digitsOnly !== e.target.value) {
                          e.target.value = digitsOnly;
                        }
                      },
                    }}
                  />
                  <span className="error-message">
                    {errors["phone"]?.message}
                  </span>
                  {/* Display custom error message */}
                  {customError && (
                    <span className="error-message">{customError}</span>
                  )}
                </div>
              </div>
            </div>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ paddingBottom: "15px !important" }}>
          <div className="popup_submit_btn">
            <ButtonUI
              loading={loading}
              variant="contained"
              buttonText="Submit"
              style={{
                backgroundColor: "#1a6b7c",
                boxShadow: "none",
                border: "1px solid #1a6b7c",
                fontWeight: "500",
                borderRadius: "4px",
                letterSpacing: "0.07rem",
                marginTop: "20px",
              }}
              handleSubmit={handleSubmit}
              onFormSubmit={onEditClientDetailsFormSubmit}
              disabled={loading}
            />
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfirmToast;
